/**
 * Analytics Service
 * 
 * Centralized service for tracking analytics events via Google Analytics 4 (gtag).
 * Provides typed methods for all event categories with automatic context injection.
 */

import type {
  AnalyticsConfig,
  AnalyticsUserProperties,
  AnalyticsEventType,
  EcommerceItem,
  defaultAnalyticsConfig,
} from './eventTypes';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
  // eslint-disable-next-line no-var
  var dataLayer: Record<string, any>[];
}

// ============================================================================
// Analytics Service Class
// ============================================================================

class AnalyticsService {
  private config: AnalyticsConfig;
  private isInitialized: boolean = false;
  private userProperties: AnalyticsUserProperties = {};
  private currentProjectId: string | null = null;
  private currentUserId: string | null = null;
  private debugMode: boolean;
  private eventQueue: Array<{ eventName: string; params: Record<string, any> }> = [];

  constructor() {
    this.config = {
      measurementId: '',
      debugMode: import.meta.env.DEV,
      enabled: true,
      sendPageViews: true,
      anonymizeIp: true,
      customDimensions: {
        project_id: 'dimension1',
        user_id_dimension: 'dimension2',
      },
    };
    this.debugMode = import.meta.env.DEV;
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  private initializationAttempted: boolean = false;
  private gtagCheckInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Initialize the analytics service with configuration
   */
  initialize(measurementId: string, options?: Partial<AnalyticsConfig>): void {
    if (this.isInitialized) {
      this.log('Analytics service already initialized');
      return;
    }

    this.initializationAttempted = true;
    this.config = {
      ...this.config,
      measurementId,
      ...options,
    };

    // Check if gtag is available
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      this.completeInitialization();
    } else {
      this.log('gtag not available yet - starting polling for availability', 'warn');
      this.startGtagPolling();
    }
  }

  /**
   * Complete the initialization once gtag is available
   */
  private completeInitialization(): void {
    if (this.isInitialized || !this.config.measurementId) return;

    this.isInitialized = true;
    this.stopGtagPolling();
    
    this.log('Analytics service initialized', { measurementId: this.config.measurementId });

    // Configure gtag
    window.gtag('config', this.config.measurementId, {
      send_page_view: this.config.sendPageViews,
      anonymize_ip: this.config.anonymizeIp,
      debug_mode: this.config.debugMode,
    });

    // Process queued events
    this.processEventQueue();
  }

  /**
   * Start polling for gtag availability
   */
  private startGtagPolling(): void {
    if (this.gtagCheckInterval) return;

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max (100ms * 50)

    this.gtagCheckInterval = setInterval(() => {
      attempts++;
      
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        this.log(`gtag became available after ${attempts * 100}ms`);
        this.completeInitialization();
      } else if (attempts >= maxAttempts) {
        this.log('gtag polling timeout - events will remain queued', 'warn');
        this.stopGtagPolling();
      }
    }, 100);
  }

  /**
   * Stop polling for gtag
   */
  private stopGtagPolling(): void {
    if (this.gtagCheckInterval) {
      clearInterval(this.gtagCheckInterval);
      this.gtagCheckInterval = null;
    }
  }

  /**
   * Manually retry initialization (useful if gtag loads late)
   */
  retryInitialization(): boolean {
    if (this.isInitialized) {
      this.log('Already initialized');
      return true;
    }

    if (!this.initializationAttempted || !this.config.measurementId) {
      this.log('Cannot retry - initialize() was not called yet', 'warn');
      return false;
    }

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      this.completeInitialization();
      return true;
    }

    this.log('gtag still not available', 'warn');
    return false;
  }

  /**
   * Process any events that were queued before initialization
   */
  private processEventQueue(): void {
    if (this.eventQueue.length > 0) {
      this.log(`Processing ${this.eventQueue.length} queued events`);
      this.eventQueue.forEach(({ eventName, params }) => {
        this.sendEvent(eventName, params);
      });
      this.eventQueue = [];
    }
  }

  // ============================================================================
  // Context Management
  // ============================================================================

  /**
   * Set the current project context
   * This sets project_id as both a user property (for user-scoped reporting)
   * and ensures it's included in all events (for event-scoped reporting)
   */
  setProjectContext(projectId: string | null): void {
    this.currentProjectId = projectId;
    if (projectId) {
      // Set as user property for GA4 user-scoped custom dimension
      this.setUserProperty('project_id', projectId);
      
      // Also push to dataLayer for GTM
      this.pushToDataLayer({
        event: 'project_context_set',
        project_id: projectId,
      });
    }
    this.log('Project context set', { projectId });
  }

  /**
   * Set the current user context
   * Sets both user_id (GA4 built-in) and user_id_dimension (custom dimension for analytics-ts queries)
   */
  setUserContext(userId: string | null, additionalProperties?: Partial<AnalyticsUserProperties>): void {
    this.currentUserId = userId;

    if (userId) {
      // Set GA4 built-in user ID
      if (window.gtag) {
        window.gtag('config', this.config.measurementId, {
          user_id: userId,
        });
      }

      // Set user_id as user property
      this.setUserProperty('user_id', userId);
      
      // IMPORTANT: Also set user_id_dimension for analytics-ts backend queries
      // The analytics-ts package queries by 'customUser:user_id_dimension'
      this.setUserProperty('user_id_dimension', userId);

      if (additionalProperties) {
        Object.entries(additionalProperties).forEach(([key, value]) => {
          if (value !== undefined) {
            this.setUserProperty(key, value);
          }
        });
      }
      
      // Push to dataLayer for GTM
      this.pushToDataLayer({
        event: 'user_context_set',
        user_id: userId,
        user_id_dimension: userId,
        ...additionalProperties,
      });
    }

    this.log('User context set', { userId, additionalProperties });
  }
  
  /**
   * Push data to GTM dataLayer
   * This ensures GTM tags can access the data
   */
  private pushToDataLayer(data: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push(data);
      this.log('DataLayer push', data);
    }
  }

  /**
   * Set a user property (custom dimension)
   * Uses gtag('set', {...}) format which is the correct way to set user properties
   */
  setUserProperty(name: string, value: string | number | boolean): void {
    this.userProperties[name as keyof AnalyticsUserProperties] = value as any;

    if (window.gtag) {
      // Correct format: gtag('set', { user_properties: { key: value } })
      window.gtag('set', {
        user_properties: {
          [name]: value,
        },
      });
    }
  }

  /**
   * Clear all user context (on logout)
   */
  clearUserContext(): void {
    this.currentUserId = null;
    this.userProperties = {};

    if (window.gtag) {
      // Correct format: gtag('set', { user_properties: { key: value } })
      window.gtag('set', {
        user_properties: {
          user_id: null,
          user_id_dimension: null,
        },
      });
    }
    
    // Also clear in dataLayer
    this.pushToDataLayer({
      event: 'user_context_cleared',
      user_id: null,
      user_id_dimension: null,
    });

    this.log('User context cleared');
  }

  // ============================================================================
  // Core Event Tracking
  // ============================================================================

  /**
   * Send a raw event to GA4 via gtag AND GTM dataLayer
   */
  private sendEvent(eventName: string, params: Record<string, any>): void {
    // Add default context - ensure project_id and user context are always present
    const enrichedParams: Record<string, any> = {
      ...params,
      project_id: params.project_id || this.currentProjectId,
      user_id_dimension: this.currentUserId || undefined,
      timestamp: new Date().toISOString(),
    };

    // Remove undefined values
    Object.keys(enrichedParams).forEach((key) => {
      if (enrichedParams[key] === undefined) {
        delete enrichedParams[key];
      }
    });

    if (!this.config.enabled) {
      this.log('Analytics disabled - event not sent', { eventName, params: enrichedParams });
      return;
    }

    // Push to GTM dataLayer first (ensures GTM tags can fire)
    this.pushToDataLayer({
      event: eventName,
      ...enrichedParams,
    });

    // Also send via gtag for direct GA4 tracking
    if (window.gtag) {
      window.gtag('event', eventName, enrichedParams);
      this.log('Event sent', { eventName, params: enrichedParams });
    } else {
      // Queue event for later
      this.eventQueue.push({ eventName, params: enrichedParams });
      this.log('Event queued (gtag not ready)', { eventName, params: enrichedParams });
    }
  }

  /**
   * Track a typed analytics event
   */
  trackEvent<T extends AnalyticsEventType>(event: T): void {
    this.sendEvent(event.event_name, event.params);
  }

  // ============================================================================
  // User Acquisition Events
  // ============================================================================

  trackUserRegistered(params: {
    method: 'public_registration' | 'invitation' | 'bulk_import' | 'manual_add';
    autoApproved: boolean;
    projectId: string;
    registrationSource?: string;
  }): void {
    this.sendEvent('user_registered', {
      method: params.method,
      auto_approved: params.autoApproved,
      project_id: params.projectId,
      registration_source: params.registrationSource,
    });
  }

  trackInvitationSent(params: {
    invitationType: 'email' | 'link' | 'group';
    projectId: string;
    recipientCount?: number;
  }): void {
    this.sendEvent('invitation_sent', {
      invitation_type: params.invitationType,
      project_id: params.projectId,
      recipient_count: params.recipientCount,
    });
  }

  trackInvitationAccepted(params: {
    invitationId: string;
    projectId: string;
    timeToAcceptHours?: number;
    invitationType?: string;
  }): void {
    this.sendEvent('invitation_accepted', {
      invitation_id: params.invitationId,
      project_id: params.projectId,
      time_to_accept_hours: params.timeToAcceptHours,
      invitation_type: params.invitationType,
    });
  }

  trackUserImported(params: {
    importCount: number;
    source: 'csv' | 'google_sheets' | 'excel' | 'manual';
    projectId: string;
    sessionId?: string;
  }): void {
    this.sendEvent('user_imported', {
      import_count: params.importCount,
      source: params.source,
      project_id: params.projectId,
      session_id: params.sessionId,
    });
  }

  trackUserAddedManual(params: {
    role: string;
    projectId: string;
    addedByRole?: string;
  }): void {
    this.sendEvent('user_added_manual', {
      role: params.role,
      project_id: params.projectId,
      added_by_role: params.addedByRole,
    });
  }

  trackUserLogin(params: {
    method: 'email' | 'phone' | 'passkey' | 'otp' | 'descope';
    projectId: string;
    isReturningUser: boolean;
  }): void {
    this.sendEvent('user_login', {
      method: params.method,
      project_id: params.projectId,
      is_returning_user: params.isReturningUser,
    });
  }

  // ============================================================================
  // Event Engagement Events
  // ============================================================================

  trackEventRsvp(params: {
    eventId: string;
    projectId: string;
    response: 'accepted' | 'declined' | 'tentative';
    eventTitle?: string;
    daysUntilEvent?: number;
  }): void {
    this.sendEvent('event_rsvp', {
      event_id: params.eventId,
      project_id: params.projectId,
      response: params.response,
      event_title: params.eventTitle,
      days_until_event: params.daysUntilEvent,
    });
  }

  trackEventPaymentCompleted(params: {
    eventId: string;
    projectId: string;
    amount: number;
    currency: string;
    paymentMethod?: string;
  }): void {
    this.sendEvent('event_payment_completed', {
      event_id: params.eventId,
      project_id: params.projectId,
      amount: params.amount,
      currency: params.currency,
      payment_method: params.paymentMethod,
    });
  }

  trackEventViewed(params: {
    eventId: string;
    projectId: string;
    eventTitle?: string;
    eventType?: string;
  }): void {
    this.sendEvent('event_viewed', {
      event_id: params.eventId,
      project_id: params.projectId,
      event_title: params.eventTitle,
      event_type: params.eventType,
    });
  }

  trackCalendarInvitationResponse(params: {
    eventId: string;
    projectId: string;
    responseType: 'accept' | 'decline' | 'tentative';
    source: 'email_link' | 'app';
  }): void {
    this.sendEvent('calendar_invitation_response', {
      event_id: params.eventId,
      project_id: params.projectId,
      response_type: params.responseType,
      source: params.source,
    });
  }

  // ============================================================================
  // Commerce Events (GA4 Enhanced Ecommerce)
  // ============================================================================

  trackViewItem(params: {
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('view_item', {
      currency: params.currency || 'USD',
      value,
      items: params.items,
      project_id: params.projectId,
    });
  }

  trackAddToCart(params: {
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('add_to_cart', {
      currency: params.currency || 'USD',
      value,
      items: params.items,
      project_id: params.projectId,
    });
  }

  trackRemoveFromCart(params: {
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('remove_from_cart', {
      currency: params.currency || 'USD',
      value,
      items: params.items,
      project_id: params.projectId,
    });
  }

  trackViewCart(params: {
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('view_cart', {
      currency: params.currency || 'USD',
      value,
      items: params.items,
      project_id: params.projectId,
    });
  }

  trackBeginCheckout(params: {
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
    coupon?: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('begin_checkout', {
      currency: params.currency || 'USD',
      value,
      items: params.items,
      project_id: params.projectId,
      coupon: params.coupon,
    });
  }

  trackPurchase(params: {
    transactionId: string;
    items: EcommerceItem[];
    currency?: string;
    projectId: string;
    tax?: number;
    shipping?: number;
    coupon?: string;
  }): void {
    const value = params.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    this.sendEvent('purchase', {
      transaction_id: params.transactionId,
      value: value + (params.tax || 0) + (params.shipping || 0),
      currency: params.currency || 'USD',
      tax: params.tax,
      shipping: params.shipping,
      items: params.items,
      project_id: params.projectId,
      coupon: params.coupon,
    });
  }

  trackDonation(params: {
    amount: number;
    currency?: string;
    recurring: boolean;
    projectId: string;
    donationType?: 'one_time' | 'monthly' | 'annual';
    campaign?: string;
  }): void {
    this.sendEvent('donation', {
      amount: params.amount,
      currency: params.currency || 'USD',
      recurring: params.recurring,
      project_id: params.projectId,
      donation_type: params.donationType,
      campaign: params.campaign,
    });
  }

  // ============================================================================
  // Content Engagement Events
  // ============================================================================

  trackChannelMessageSent(params: {
    channelId: string;
    projectId: string;
    messageType?: 'text' | 'image' | 'file';
    hasAttachments?: boolean;
  }): void {
    this.sendEvent('channel_message_sent', {
      channel_id: params.channelId,
      project_id: params.projectId,
      message_type: params.messageType,
      has_attachments: params.hasAttachments,
    });
  }

  trackMailSent(params: {
    projectId: string;
    recipientCount: number;
    mailType: 'individual' | 'broadcast' | 'channel';
    hasAttachments?: boolean;
  }): void {
    this.sendEvent('mail_sent', {
      project_id: params.projectId,
      recipient_count: params.recipientCount,
      mail_type: params.mailType,
      has_attachments: params.hasAttachments,
    });
  }

  trackBlogPostViewed(params: {
    postId: string;
    projectId: string;
    postTitle?: string;
    authorId?: string;
    readTimeSeconds?: number;
  }): void {
    this.sendEvent('blog_post_viewed', {
      post_id: params.postId,
      project_id: params.projectId,
      post_title: params.postTitle,
      author_id: params.authorId,
      read_time_seconds: params.readTimeSeconds,
    });
  }

  trackMediaViewed(params: {
    mediaId: string;
    projectId: string;
    mediaType: 'image' | 'video' | 'document' | 'audio';
    fileSizeKb?: number;
  }): void {
    this.sendEvent('media_viewed', {
      media_id: params.mediaId,
      project_id: params.projectId,
      media_type: params.mediaType,
      file_size_kb: params.fileSizeKb,
    });
  }

  trackDirectorySearched(params: {
    projectId: string;
    queryLength: number;
    resultsCount: number;
    searchType?: 'public' | 'map' | 'all';
    filtersApplied?: string[];
  }): void {
    this.sendEvent('directory_searched', {
      project_id: params.projectId,
      query_length: params.queryLength,
      results_count: params.resultsCount,
      search_type: params.searchType,
      filters_applied: params.filtersApplied?.join(','),
    });
  }

  trackProfileViewed(params: {
    profileUserId: string;
    projectId: string;
    viewerRelationship?: 'self' | 'admin' | 'member';
    viewSource?: 'directory' | 'search' | 'direct';
  }): void {
    this.sendEvent('profile_viewed', {
      profile_user_id: params.profileUserId,
      project_id: params.projectId,
      viewer_relationship: params.viewerRelationship,
      view_source: params.viewSource,
    });
  }

  // ============================================================================
  // Navigation & Engagement Events
  // ============================================================================

  trackPageView(params: {
    pageTitle: string;
    pageLocation: string;
    pagePath: string;
    projectId?: string;
  }): void {
    this.sendEvent('page_view', {
      page_title: params.pageTitle,
      page_location: params.pageLocation,
      page_path: params.pagePath,
      project_id: params.projectId || this.currentProjectId,
    });
  }

  trackFeatureUsed(params: {
    featureName: string;
    projectId: string;
    featureCategory?: string;
    action?: string;
  }): void {
    this.sendEvent('feature_used', {
      feature_name: params.featureName,
      project_id: params.projectId,
      feature_category: params.featureCategory,
      action: params.action,
    });
  }

  trackSessionStart(params?: {
    projectId?: string;
    sessionId?: string;
  }): void {
    this.sendEvent('session_start', {
      project_id: params?.projectId || this.currentProjectId,
      session_id: params?.sessionId,
    });
  }

  // ============================================================================
  // Error & Performance Events
  // ============================================================================

  trackError(params: {
    errorType: 'api' | 'validation' | 'auth' | 'network' | 'unknown';
    errorMessage: string;
    errorCode?: string;
    projectId?: string;
    pagePath?: string;
  }): void {
    this.sendEvent('error', {
      error_type: params.errorType,
      error_message: params.errorMessage.substring(0, 100), // Truncate long messages
      error_code: params.errorCode,
      project_id: params.projectId || this.currentProjectId,
      page_path: params.pagePath || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    });
  }

  trackPerformance(params: {
    metricName: string;
    value: number;
    projectId?: string;
    pagePath?: string;
  }): void {
    this.sendEvent('performance', {
      metric_name: params.metricName,
      value: Math.round(params.value),
      project_id: params.projectId || this.currentProjectId,
      page_path: params.pagePath || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    });
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Enable or disable analytics
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.log(`Analytics ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(debug: boolean): void {
    this.debugMode = debug;
    this.config.debugMode = debug;
  }

  /**
   * Get current configuration
   */
  getConfig(): AnalyticsConfig {
    return { ...this.config };
  }

  /**
   * Check if analytics is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Log helper for debug mode
   */
  private log(message: string, data?: any, level: 'log' | 'warn' | 'error' = 'log'): void {
    if (this.debugMode) {
      const prefix = '[Analytics]';
      if (data) {
        console[level](prefix, message, data);
      } else {
        console[level](prefix, message);
      }
    }
  }
  
  // ============================================================================
  // Debug & Verification Utilities
  // ============================================================================
  
  /**
   * Run a comprehensive verification of the analytics setup
   * Call this from console: window.analyticsService.verify()
   */
  verify(): { 
    gtmLoaded: boolean; 
    gtagReady: boolean; 
    dataLayerExists: boolean;
    measurementId: string | null;
    projectId: string | null;
    userId: string | null;
    isInitialized: boolean;
    queuedEvents: number;
    recommendations: string[];
  } {
    const recommendations: string[] = [];
    
    const gtmLoaded = typeof window !== 'undefined' && 
      Array.isArray(window.dataLayer) && 
      window.dataLayer.some((item: any) => item['gtm.start']);
    
    const gtagReady = typeof window !== 'undefined' && typeof window.gtag === 'function';
    const dataLayerExists = typeof window !== 'undefined' && Array.isArray(window.dataLayer);
    
    if (!gtmLoaded) {
      recommendations.push('GTM container not detected. Check that GTM scripts are in index.html and VITE_GOOGLE_TAG_MANAGER_ID is set.');
    }
    
    if (!gtagReady) {
      recommendations.push('gtag function not available. Firebase Analytics or GA4 config tag may not be loading.');
    }
    
    if (!this.isInitialized) {
      recommendations.push('Analytics service not initialized. Call analyticsService.initialize(measurementId) or check VITE_FIREBASE_MEASUREMENT_ID.');
    }
    
    if (!this.currentProjectId) {
      recommendations.push('No project context set. Analytics events will not be filterable by project. Call setProjectContext(projectId).');
    }
    
    if (!this.currentUserId) {
      recommendations.push('No user context set. User-level analytics will not be available. This is expected if user is not logged in.');
    }
    
    if (this.eventQueue.length > 0) {
      recommendations.push(`${this.eventQueue.length} events are queued waiting for gtag. These will be sent once gtag is ready.`);
    }
    
    const result = {
      gtmLoaded,
      gtagReady,
      dataLayerExists,
      measurementId: this.config.measurementId || null,
      projectId: this.currentProjectId,
      userId: this.currentUserId,
      isInitialized: this.isInitialized,
      queuedEvents: this.eventQueue.length,
      recommendations,
    };
    
    // Log formatted results
    console.group('🔍 Analytics Verification');
    console.log('GTM Loaded:', gtmLoaded ? '✅' : '❌');
    console.log('gtag Ready:', gtagReady ? '✅' : '❌');
    console.log('DataLayer Exists:', dataLayerExists ? '✅' : '❌');
    console.log('Measurement ID:', this.config.measurementId || '❌ Not set');
    console.log('Project ID:', this.currentProjectId || '⚠️ Not set');
    console.log('User ID:', this.currentUserId || '⚠️ Not set (expected if not logged in)');
    console.log('Service Initialized:', this.isInitialized ? '✅' : '❌');
    console.log('Queued Events:', this.eventQueue.length);
    
    if (recommendations.length > 0) {
      console.group('📋 Recommendations');
      recommendations.forEach((rec, i) => console.log(`${i + 1}. ${rec}`));
      console.groupEnd();
    } else {
      console.log('✅ All checks passed!');
    }
    console.groupEnd();
    
    return result;
  }
  
  /**
   * Send a test event to verify tracking is working
   * Call this from console: window.analyticsService.sendTestEvent()
   */
  sendTestEvent(): void {
    const testParams = {
      test_timestamp: new Date().toISOString(),
      test_project_id: this.currentProjectId || 'test_project',
      test_user_id: this.currentUserId || 'anonymous',
    };
    
    console.log('📤 Sending test event with params:', testParams);
    this.sendEvent('analytics_test', testParams);
    
    console.log('✅ Test event sent! Check:');
    console.log('  1. GTM Preview mode (if enabled)');
    console.log('  2. GA4 DebugView: https://analytics.google.com/analytics/web/ → Admin → DebugView');
    console.log('  3. GA4 Realtime: https://analytics.google.com/analytics/web/ → Reports → Realtime');
  }
  
  /**
   * Get current dataLayer contents for debugging
   */
  getDataLayer(): any[] {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      console.log('📊 DataLayer contents:', window.dataLayer);
      return [...window.dataLayer];
    }
    console.warn('⚠️ DataLayer not found');
    return [];
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const analyticsService = new AnalyticsService();

// Export for debugging in console
if (typeof window !== 'undefined') {
  (window as any).analyticsService = analyticsService;
  
  // Add convenient global debug functions
  (window as any).verifyAnalytics = () => analyticsService.verify();
  (window as any).testAnalytics = () => analyticsService.sendTestEvent();
  
  console.log('🔧 Analytics debug tools available:');
  console.log('  • window.verifyAnalytics() - Check analytics setup');
  console.log('  • window.testAnalytics() - Send a test event');
  console.log('  • window.analyticsService - Full service access');
}

export default analyticsService;

